import { useState, useEffect, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y } from 'swiper/modules'
import type { Banner } from '../services/bannersService'
import { fetchBanners } from '../services/bannersService'
import { Props_SnippetBrandsCarousel } from './BrandsCarousel.props'
import { formatHtmlContent } from '../../../../Shared/AttraxTs/React/RenderReactDangerousHtml'
import SwiperControls, { swiperNavigationOptions, swiperPaginationOptions } from '../Shared/SwiperControls/SwiperControls'
import BrandsCarouselSkeleton from './BrandsCarouselSkeleton'

const SnippetBrandsCarousel = (props: Props_SnippetBrandsCarousel) => {
    const { heading } = props
    const classHeadingVisibility: string = heading.visibility?.value === "hidden" ? ` snippet-brands-carousel__heading--${heading.visibility.value}` : ''

    const [brands, setBrands] = useState<Banner[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const snippetRef = useRef<HTMLElement | null>(null)

    const carouselId = "brands-banner"

    useEffect(() => {
        const loadBanners = async () => {
            setIsLoading(true)

            try {
                const bannerData = await fetchBanners(carouselId, true)
                setBrands(bannerData)
            } catch (error) {
                console.error("Failed to load banners in component: Snippet Brands Carousel.", error)
                setBrands([])
            } finally {
                setIsLoading(false)
            }
        }

        loadBanners()
    }, [carouselId])

    if (isLoading) {
        return <BrandsCarouselSkeleton />
    }

    if (!isLoading && brands.length === 0) {
        return (
            <section key={carouselId} ref={snippetRef} className="snippet-brands-carousel">
                <div className="snippet-brands-carousel__container">
                    <h2 className={`snippet-brands-carousel__heading${classHeadingVisibility}`}>{heading.content.value}</h2>
                    <div data-testid="swiper" className="snippet-brands-carousel__swiper"></div>
                </div>
            </section>
        )
    }

    return (
        <section key={carouselId} ref={snippetRef} className="snippet-brands-carousel">
            <div className="snippet-brands-carousel__container">
                <h2 className={`snippet-brands-carousel__heading${classHeadingVisibility}`}>{heading.content.value}</h2>

                <Swiper
                    className="snippet-brands-carousel__swiper"
                    modules={[Navigation, Pagination, A11y]}
                    navigation={{
                        ...swiperNavigationOptions,
                        prevEl: `.swiper-navigation__button-prev-${carouselId}`,
                        nextEl: `.swiper-navigation__button-next-${carouselId}`,
                    }}
                    pagination={{
                        ...swiperPaginationOptions,
                        el: `.swiper-pagination-${carouselId}`,
                    }}
                    spaceBetween={12}
                    slidesPerView={1}
                    slidesPerGroup={1}
                    breakpoints={{
                        479: {
                            slidesPerView: 2,
                            slidesPerGroup: 2,
                        },
                        991: {
                            slidesPerView: 3,
                            slidesPerGroup: 3,
                        },
                        1200: {
                            slidesPerView: 4,
                            slidesPerGroup: 4,
                        },
                    }}
                    watchOverflow={true}
                    watchSlidesProgress={true}
                >
                    {brands?.map((banner, index) => (
                        <SwiperSlide key={banner.id || index} className="snippet-brands-carousel__slide">
                            <img
                                src={`/img/${banner.imageId}`}
                                alt={banner.altTagText}
                                className="snippet-brands-carousel__image"
                                loading="lazy"
                                width={225}
                                height={225}
                            />
                            {banner.customHtml && (
                                <div className="snippet-brands-carousel__custom-html" dangerouslySetInnerHTML={{ __html: formatHtmlContent(banner.customHtml) }} />
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>

                <SwiperControls 
                    uniqueId={carouselId} 
                    className="snippet-brands-carousel__navigation"
                />
            </div>
        </section>
    )
}

export default SnippetBrandsCarousel